import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { apiGet, apiPost } from "../lib/api";
import type { OutreachRecipient, OutreachSendStatus, OutreachStatus } from "../lib/types";
import { OutreachStatusStrip } from "../components/OutreachStatusStrip";
import { useScope } from "../scope/ScopeContext";
import { dateWindowOptions } from "../lib/date-windows";
import { skipReasonLabel } from "../lib/skip-reasons";

/**
 * Outreach approval queue — pending recipients awaiting a human OK before the
 * worker sends. Also browsable by status for a general recipient list.
 */

const STATUS_OPTIONS: { value: OutreachSendStatus | ""; label: string; title: string }[] = [
  { value: "pending", label: "Pending", title: "Waiting for a human to approve" },
  { value: "approved", label: "Approved", title: "Approved and queued for the sender" },
  { value: "sent", label: "Sent", title: "Message went out" },
  { value: "skipped", label: "Skipped", title: "A person took them back out, or the system chose not to ask" },
  { value: "suppressed", label: "Suppressed", title: "Blocked by a rule — consent, opt-out, or a recent ask" },
  { value: "failed", label: "Failed", title: "The carrier or mail provider rejected it" },
  { value: "", label: "All", title: "Every status" },
];

/** Chip count for a status, or the sum of every status for the "All" chip. */
function countFor(counts: Record<string, number>, value: OutreachSendStatus | ""): number {
  if (value) return counts[value] ?? 0;
  return Object.values(counts).reduce((a, b) => a + b, 0);
}

type SortKey =
  | "customer"
  | "store"
  | "type"
  | "eventDate"
  | "status"
  | "survey"
  | "updated";

/**
 * One sortable value per column. Nulls sort last in either direction — a row
 * with no answer yet is not "older than everything", it is simply unknown, and
 * burying it under real data in ascending order would hide the newest work.
 */
function sortValue(r: OutreachRecipient, key: SortKey): string | number | null {
  switch (key) {
    case "customer":
      return ([r.firstName, r.lastName].filter(Boolean).join(" ") || "").toLowerCase();
    case "store":
      return (r.dealerName ?? "").toLowerCase();
    case "type":
      return r.type ?? "";
    case "eventDate":
      return r.eventDate ? Date.parse(r.eventDate) : null;
    case "status":
      return r.sendStatus ?? "";
    case "survey":
      // Order by how far they got, not alphabetically: not sent < sent < opened
      // < answered. That makes one click surface everyone who actually replied.
      if (r.respondedAt) return 3;
      if (r.firstClickedAt) return 2;
      if (r.sentAt) return 1;
      return 0;
    case "updated":
      return r.updatedAt ? Date.parse(r.updatedAt) : null;
  }
}

function compareBy(key: SortKey, dir: "asc" | "desc") {
  const sign = dir === "asc" ? 1 : -1;
  return (a: OutreachRecipient, b: OutreachRecipient) => {
    const av = sortValue(a, key);
    const bv = sortValue(b, key);
    if (av === null && bv === null) return 0;
    if (av === null) return 1;
    if (bv === null) return -1;
    if (av < bv) return -1 * sign;
    if (av > bv) return 1 * sign;
    return 0;
  };
}

/** A header cell that sorts, and says which way it is sorting. */
function SortHeader({
  label,
  col,
  sortKey,
  sortDir,
  onSort,
  align,
}: {
  label: string;
  col: SortKey;
  sortKey: SortKey;
  sortDir: "asc" | "desc";
  onSort: (k: SortKey) => void;
  align?: "right";
}) {
  const active = sortKey === col;
  return (
    <th className={`px-3 py-2 ${align === "right" ? "text-right" : ""}`}>
      <button
        type="button"
        onClick={() => onSort(col)}
        aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
        className={`inline-flex items-center gap-1 rounded hover:text-slate-900 ${
          active ? "font-semibold text-slate-900" : ""
        }`}
      >
        {label}
        <span aria-hidden="true" className={active ? "text-slate-500" : "text-slate-300"}>
          {active ? (sortDir === "asc" ? "\u2191" : "\u2193") : "\u2195"}
        </span>
      </button>
    </th>
  );
}

/**
 * How far the customer got, which is a different axis from whether we managed
 * to send: a row can be `sent` for a week and never opened. Don asked for
 * Sent → Answered as its own status after not being able to tell those apart,
 * and it is also why an already-sent recipient is hard to find in here.
 *
 * Answered reads back as Don's own Yes/No rather than the stored score — the
 * campaign's threshold decides which it was, so a campaign tuned differently
 * still reads correctly.
 */
function SurveyBadge({ recipient }: { recipient: OutreachRecipient }) {
  const t = recipient.campaignSatisfactionThreshold ?? 4;
  let label: string;
  let tone: string;
  if (recipient.respondedAt && recipient.satisfactionScore != null) {
    const happy = recipient.satisfactionScore >= t;
    label = happy ? "Answered · Yes" : "Answered · No";
    tone = happy
      ? "border-emerald-200 bg-emerald-50 text-emerald-700"
      : "border-rose-200 bg-rose-50 text-rose-700";
  } else if (recipient.respondedAt) {
    label = "Answered";
    tone = "border-slate-200 bg-slate-50 text-slate-600";
  } else if (recipient.firstClickedAt) {
    label = "Opened";
    tone = "border-amber-200 bg-amber-50 text-amber-700";
  } else if (recipient.sentAt) {
    label = "No answer yet";
    tone = "border-slate-200 bg-white text-slate-500";
  } else {
    return <span className="text-xs text-slate-400">—</span>;
  }
  return (
    <span className={`inline-block rounded-full border px-2 py-0.5 text-[11px] font-medium ${tone}`}>
      {label}
    </span>
  );
}

const SORT_LABEL: Record<SortKey, string> = {
  customer: "Customer",
  store: "Store",
  type: "Type",
  eventDate: "Event date",
  status: "Status",
  survey: "Survey",
  updated: "Last updated",
};

function TypeChip({ type }: { type: OutreachRecipient["type"] }) {
  return (
    <span
      className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase ring-1 ring-inset ${
        type === "sales"
          ? "bg-amber-50 text-amber-700 ring-amber-200"
          : "bg-emerald-50 text-emerald-700 ring-emerald-200"
      }`}
    >
      {type}
    </span>
  );
}

/** Rows per page. The rest arrives via "Load more", never silently omitted. */
const PAGE_SIZE = 200;

interface RecipientsResponse {
  recipients: OutreachRecipient[];
  olderCount: number | null;
  statusCounts?: Record<string, number>;
  total?: number;
  hasMore?: boolean;
  nextOffset?: number | null;
}

export function OutreachApprovalPage() {
  const { focusedDealerId } = useScope();
  const [status, setStatus] = useState<OutreachSendStatus | "">("pending");
  const [typeFilter, setTypeFilter] = useState<"" | "sales" | "service">("");
  const [recipients, setRecipients] = useState<OutreachRecipient[]>([]);
  // Age of the sale/service that earned the ask, not of the queue row. Starts at
  // "any date" on purpose: a default window is exactly what lib/review_window.ts
  // warns against, because a queue that silently arrives shortened reads as a
  // queue with nothing in it. Narrowing is one click, and the thing that actually
  // guards against a repeat of the 2026-08-27 batch is the staleness line on the
  // approve confirmation below, which fires whatever this is set to.
  const [withinDays, setWithinDays] = useState<number | null>(null);
  // "Older" flips the window rather than removing it, so the backlog is exactly
  // the complement and stays one click away instead of vanishing.
  const [showOlder, setShowOlder] = useState(false);
  const [olderCount, setOlderCount] = useState<number | null>(null);
  // Paging. This page asked for 200 rows and said nothing about the rest; with
  // 1,291 pending recipients everything past the first 200 was unreachable — on
  // the screen where a human clicks Approve and a text goes out.
  const [total, setTotal] = useState(0);
  const [nextOffset, setNextOffset] = useState<number | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  // Per-status totals under the current dealer/type/window filters, from the
  // same request as the rows. Drawn on the status chips so every state and its
  // size is visible at once — a dropdown hid both (Kat, 2026-09-13).
  const [statusCounts, setStatusCounts] = useState<Record<string, number>>({});
  // Sorting is client-side on purpose: the queue loads a bounded page (200), so
  // re-sorting is instant and costs no round trip. If that limit ever grows,
  // this moves to an ORDER BY on the server.
  const [sortKey, setSortKey] = useState<SortKey>("eventDate");
  const [sortDir, setSortDir] = useState<"asc" | "desc">("desc");
  const toggleSort = (key: SortKey) => {
    if (key === sortKey) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
    } else {
      setSortKey(key);
      setSortDir(key === "customer" || key === "store" || key === "type" ? "asc" : "desc");
    }
  };
  const sortedRecipients = useMemo(
    () => [...recipients].sort(compareBy(sortKey, sortDir)),
    [recipients, sortKey, sortDir]
  );
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  // The live state of the sender/campaign/window gates, from the status strip.
  // Used to decide whether approving needs a confirmation — it only does when
  // approving would actually put messages on the wire shortly.
  const [outreachStatus, setOutreachStatus] = useState<OutreachStatus | null>(null);

  // See OutreachStatusStrip: overlapping requests, slowest wins, wrong rooftop.
  const reqId = useRef(0);

  const buildParams = useCallback(
    (offset: number) => {
      const params = new URLSearchParams();
      if (focusedDealerId) params.set("dealerId", focusedDealerId);
      if (status) params.set("status", status);
      if (typeFilter) params.set("type", typeFilter);
      if (withinDays !== null) {
        params.set("withinDays", String(withinDays));
        if (showOlder) params.set("olderOnly", "1");
      }
      // Order by event date, not insertion time: this page filters on event date
      // and pages, so the SQL order decides which rows exist to filter. The
      // feedback inbox and recognition list deliberately do NOT pass this.
      params.set("sort", "event");
      params.set("limit", String(PAGE_SIZE));
      if (offset > 0) params.set("offset", String(offset));
      return params;
    },
    [focusedDealerId, status, typeFilter, withinDays, showOlder]
  );

  const load = useCallback(async () => {
    const mine = ++reqId.current;
    setLoading(true);
    try {
      const res = await apiGet<RecipientsResponse>(
        `/api/outreach/recipients?${buildParams(0).toString()}`
      );
      if (mine !== reqId.current) return; // superseded by a newer filter/store
      setRecipients(res.recipients);
      setStatusCounts(res.statusCounts ?? {});
      setTotal(res.total ?? res.recipients.length);
      setNextOffset(res.nextOffset ?? null);
      // Don't overwrite the count while showing the older set — that response
      // withholds nothing, so it reports null, and taking it would blank the chip
      // the user is standing on.
      if (!showOlder) setOlderCount(res.olderCount ?? null);
      setSelected(new Set());
    } catch (err) {
      console.error("recipients load failed:", err);
    } finally {
      if (mine === reqId.current) setLoading(false);
    }
  }, [buildParams, showOlder]);

  const loadMore = useCallback(async () => {
    if (nextOffset === null || loadingMore) return;
    const mine = reqId.current;
    setLoadingMore(true);
    try {
      const res = await apiGet<RecipientsResponse>(
        `/api/outreach/recipients?${buildParams(nextOffset).toString()}`
      );
      // A filter changed while this page was in flight — appending it would mix
      // two different queries into one list. Drop it; `load` owns the new one.
      if (mine !== reqId.current) return;
      // De-duplicate by id: rows shift as recipients are approved or pulled, so
      // the same person can otherwise arrive on two consecutive pages.
      setRecipients((prev) => {
        const seen = new Set(prev.map((r) => r.id));
        return [...prev, ...res.recipients.filter((r) => !seen.has(r.id))];
      });
      setTotal(res.total ?? 0);
      setNextOffset(res.nextOffset ?? null);
      setStatusCounts(res.statusCounts ?? {});
    } catch (err) {
      console.error("recipients page load failed:", err);
    } finally {
      setLoadingMore(false);
    }
  }, [buildParams, nextOffset, loadingMore]);

  useEffect(() => {
    void load();
  }, [load]);

  const allSelected = useMemo(
    () => recipients.length > 0 && recipients.every((r) => selected.has(r.id)),
    [recipients, selected]
  );

  /**
   * How much of the current selection is being asked about a visit older than
   * its own campaign's window — null when none of it is, so callers can treat
   * "nothing to say" and "say this" as one check.
   *
   * `isStale` and `eventAgeDays` are both computed server-side from
   * lib/review_window.ts, so this counts a verdict rather than re-deriving one:
   * the threshold is each campaign's own recency_window_days, not a number
   * picked here that could drift away from what the warehouse pull enforces.
   */
  const staleSummary = useMemo(() => {
    const chosen = recipients.filter((r) => selected.has(r.id));
    const stale = chosen.filter((r) => r.isStale);
    if (stale.length === 0) return null;
    const ages = stale.map((r) => r.eventAgeDays).filter((d): d is number => typeof d === "number");
    const oldest = ages.length > 0 ? Math.max(...ages) : null;
    const undated = stale.length - ages.length;
    const parts = [
      `${stale.length} of these ${chosen.length} had their visit longer ago than the campaign asks for`,
    ];
    if (oldest !== null) parts.push(`oldest is ${oldest} days`);
    if (undated > 0) parts.push(`${undated} carry no date at all`);
    return { count: stale.length, oldest, sentence: `${parts.join(" — ")}.` };
  }, [recipients, selected]);

  const toggleAll = () => {
    setSelected(allSelected ? new Set() : new Set(recipients.map((r) => r.id)));
  };

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const act = async (action: "approve" | "skip" | "unskip", ids: string[]) => {
    if (ids.length === 0 || busy) return;
    setBusy(true);
    try {
      await apiPost(`/api/outreach/recipients/${action}`, { recipientIds: ids });
      await load();
    } catch (err) {
      alert(`${action} failed: ${err instanceof Error ? err.message : String(err)}`);
    } finally {
      setBusy(false);
    }
  };
  /**
   * Approving is the step people misread. It does not send — it hands these rows
   * to the sender. But when the sender is running and the window is open, "hands
   * to the sender" means "these people get messaged shortly", so confirm THEN and
   * only then. A confirmation that fires when nothing can send trains people to
   * click through it.
   */
  const approveSelected = async () => {
    if (selected.size === 0 || busy) return;
    const n = selected.size;
    const sendingIsLive = !!outreachStatus && !outreachStatus.approvalIsSafe;
    // Staleness is worth a stop even when nothing can send today, because the
    // damage is deferred, not avoided: on 2026-08-27 a 201-row batch was approved
    // against a sender that was switched off, and 193 of those rows were older
    // than the week the batch was meant to cover. By the time the sender is armed
    // nobody re-reads the queue. So this confirms on its own trigger.
    const staleLine = staleSummary ? `${staleSummary.sentence}\n\n` : "";
    if (sendingIsLive || staleSummary) {
      const liveLine = sendingIsLive
        ? "Sending is live right now, so they will be messaged shortly. There is no second confirmation after this."
        : "They will not be messaged until sending is switched on — but nobody re-reads the queue at that point, so decide now.";
      if (!window.confirm(`Approve ${n} ${n === 1 ? "person" : "people"}?\n\n${staleLine}${liveLine}`))
        return;
    }
    await act("approve", [...selected]);
  };
  const skipSelected = () => act("skip", [...selected]);
  // Undo for a mis-click, and only that. The server refuses any row the SYSTEM
  // skipped (already reviewed, cooldown, suppressed…) — see services/unskip.ts —
  // so this only offers the button where it can actually work.
  const unskipSelected = () => act("unskip", [...selected]);
  const selectedUndoable = recipients.filter(
    (r) => selected.has(r.id) && r.sendStatus === "skipped" && r.skipReason === "manual"
  ).length;

  // Individual immediate send — bypasses the trickle/business-hours window, but
  // NOT the campaign switch or the approval. The server enforces both
  // (services/comms/send_now_guard.ts); this only decides what to offer.
  const canSendNow = (r: OutreachRecipient) =>
    r.campaignMode !== undefined &&
    r.campaignMode !== "off" &&
    ["approved", "sent", "partial", "failed"].includes(r.sendStatus);

  const whyNotSendable = (r: OutreachRecipient) =>
    r.sendStatus === "sending"
      ? "This message is already on its way."
      : r.campaignMode === "off"
        ? "This campaign is turned off — nothing sends from it, including Send now."
        : "Approve this customer first. Send now skips the waiting, not the approval.";

  /** Two or three words naming the shut lock, shown in place of a dead button. */
  const notSendableLabel = (r: OutreachRecipient) =>
    r.sendStatus === "sending"
      ? "Sending…"
      : r.campaignMode === "off"
        ? "Campaign off"
        : "Needs approval";

  const sendNow = async (r: OutreachRecipient) => {
    if (busy || !canSendNow(r)) return;
    const who = [r.firstName, r.lastName].filter(Boolean).join(" ") || r.email || "this customer";
    if (
      !window.confirm(
        `Message ${who} right now?\n\nThis is a real customer and it sends immediately — ` +
          `it does not wait for the trickle or business hours.`
      )
    )
      return;
    setBusy(true);
    try {
      await apiPost(`/api/outreach/recipients/${r.id}/send-now`, {});
      await load();
    } catch (err) {
      // The server's refusals carry a plain-English `message`; prefer it.
      const detail = err instanceof Error ? err.message : String(err);
      alert(detail);
    } finally {
      setBusy(false);
    }
  };

  // Shared by the table row (md+) and the card (below md).
  const rowAction = (r: OutreachRecipient) => {
    if (r.sendStatus === "skipped" || r.sendStatus === "suppressed") return null;
    return canSendNow(r) ? (
      <button
        onClick={() => void sendNow(r)}
        disabled={busy}
        className="rounded-lg border border-brand-200 bg-brand-50 px-2.5 py-1 text-xs font-medium text-brand-700 hover:bg-brand-100 disabled:cursor-not-allowed disabled:opacity-40"
        title="Send this customer the review request immediately (skips the trickle)"
      >
        {r.sendStatus === "sent" || r.sendStatus === "partial" ? "Resend" : "Send now"}
      </button>
    ) : (
      // A greyed-out button reads as "broken". Say which lock is shut.
      <span className="cursor-help text-xs text-slate-400" title={whyNotSendable(r)}>
        {notSendableLabel(r)}
      </span>
    );
  };

  const selectionActionable =
    selected.size > 0 && (status === "pending" || status === "approved");

  return (
    <div className="mx-auto max-w-6xl px-4 py-6 pb-24 sm:px-8 sm:py-8 md:pb-8">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">Outreach queue</h1>
          <p className="mt-1 max-w-3xl text-sm text-slate-500">
            Everyone here is a real customer waiting on a human.{" "}
            <span className="font-medium text-slate-700">Approve</span> hands people to the sender —
            it is not a draft step and there is no second confirmation later; the sender then
            releases them a few at a time during business hours, never as one blast.{" "}
            <span className="font-medium text-slate-700">Send now</span> messages one person
            immediately, skipping the wait but not the approval.{" "}
            <span className="font-medium text-slate-700">Skip</span> takes someone back out.
            Skipped rows show why the system chose not to ask.
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as "" | "sales" | "service")}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
          >
            <option value="">Sales + Service</option>
            <option value="sales">Sales</option>
            <option value="service">Service</option>
          </select>
          <select
            value={withinDays === null ? "all" : String(withinDays)}
            onChange={(e) => {
              const v = e.target.value;
              setWithinDays(v === "all" ? null : Number(v));
              // "All" has no complement to show, so leaving showOlder set would
              // strand the view on a chip that is no longer rendered.
              if (v === "all") setShowOlder(false);
            }}
            className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
            title="How recent the sale or service visit was"
          >
            {dateWindowOptions(showOlder).map((o) => (
              <option key={o.days} value={String(o.days)}>
                {o.label}
              </option>
            ))}
            <option value="all">Any date</option>
          </select>
          {withinDays !== null && (showOlder || (olderCount ?? 0) > 0) && (
            <button
              onClick={() => setShowOlder((v) => !v)}
              className={
                showOlder
                  ? "rounded-lg bg-slate-700 px-2 py-1.5 text-xs font-medium text-white"
                  : "rounded-lg border border-slate-300 px-2 py-1.5 text-xs text-slate-600 hover:bg-slate-100"
              }
              title={`${olderCount} ${olderCount === 1 ? "person" : "people"} whose visit was more than ${withinDays} days ago`}
            >
              Older · {olderCount}
            </button>
          )}
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {STATUS_OPTIONS.map((o) => {
          const active = status === o.value;
          const n = countFor(statusCounts, o.value);
          return (
            <button
              key={o.value || "all"}
              onClick={() => setStatus(o.value)}
              title={o.title}
              className={`rounded-full px-2.5 py-1 text-xs font-medium transition ${
                active
                  ? "bg-brand-600 text-white"
                  : "bg-slate-100 text-slate-700 hover:bg-slate-200"
              }`}
            >
              {o.label}
              {n > 0 ? ` · ${n}` : ""}
            </button>
          );
        })}
      </div>

      {withinDays !== null && showOlder && (
        <p className="mt-2 text-xs text-amber-700">
          Showing only people whose visit was more than {withinDays} days ago. A review request
          this long after the fact gets a weaker response — approve deliberately.
        </p>
      )}

      <OutreachStatusStrip onStatus={setOutreachStatus} />

      {(status === "pending" || status === "approved") && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          {status === "pending" && (
            <button
              onClick={() => void approveSelected()}
              disabled={selected.size === 0 || busy}
              className="hidden rounded-lg bg-brand-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-brand-700 disabled:opacity-40 md:inline-block"
            >
              Approve selected ({selected.size})
            </button>
          )}
          {staleSummary && (
            <span className="rounded-lg bg-amber-50 px-2 py-1.5 text-xs text-amber-800">
              {staleSummary.sentence}
            </span>
          )}
          <button
            onClick={() => void skipSelected()}
            disabled={selected.size === 0 || busy}
            className="hidden rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 disabled:opacity-40 md:inline-block"
          >
            {status === "approved" ? `Remove from queue (${selected.size})` : "Skip selected"}
          </button>
          <span className="max-w-xl text-xs text-slate-400">
            {status === "approved"
              ? "These people are approved and waiting for the sender. Removing takes them back out — nothing has been sent to them yet."
              : "Approving does not send anything by itself. It hands these people to the sender."}
          </span>
        </div>
      )}

      {status === "skipped" && (
        <div className="mt-4 flex flex-wrap items-center gap-2">
          <button
            onClick={() => void unskipSelected()}
            disabled={busy || selectedUndoable === 0}
            title={
              selected.size > 0 && selectedUndoable === 0
                ? "The system skipped these, not a person — putting them back would override an eligibility rule."
                : undefined
            }
            className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-700 hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40"
          >
            {selectedUndoable > 0 ? `Put back in queue (${selectedUndoable})` : "Put back in queue"}
          </button>
          <span className="max-w-xl text-xs text-slate-400">
            Undo for a mis-click. Rows the system skipped — already reviewed, asked recently,
            suppressed — can't be put back from here; that would override an eligibility rule.
          </span>
        </div>
      )}

      {/* Phone: one card per person. Same data and actions as the table below. */}
      <div className="mt-4 md:hidden">
        <div className="flex items-center justify-between gap-2">
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input type="checkbox" checked={allSelected} onChange={toggleAll} className="h-4 w-4" />
            Select all shown
          </label>
          <label className="flex items-center gap-1.5 text-xs text-slate-500">
            Sort
            <select
              value={sortKey}
              onChange={(e) => toggleSort(e.target.value as SortKey)}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs"
            >
              {(Object.keys(SORT_LABEL) as SortKey[]).map((k) => (
                <option key={k} value={k}>
                  {SORT_LABEL[k]}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={() => setSortDir((d) => (d === "asc" ? "desc" : "asc"))}
              aria-label={sortDir === "asc" ? "Sorted ascending" : "Sorted descending"}
              className="rounded-lg border border-slate-300 px-2 py-1.5 text-xs text-slate-600"
            >
              {sortDir === "asc" ? "\u2191" : "\u2193"}
            </button>
          </label>
        </div>
        {loading && <div className="mt-3 text-center text-sm text-slate-500">Loading…</div>}
        {!loading && recipients.length === 0 && (
          <div className="mt-3 rounded-xl border border-dashed border-slate-300 bg-white px-5 py-8 text-center text-sm text-slate-500">
            Nothing here.
          </div>
        )}
        <ul className="mt-3 space-y-2">
          {sortedRecipients.map((r) => {
            const checked = selected.has(r.id);
            return (
              <li
                key={r.id}
                className={`rounded-xl border bg-white p-3 shadow-sm ${
                  checked ? "border-brand-500 ring-1 ring-brand-200" : "border-slate-200"
                }`}
              >
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => toggle(r.id)}
                    aria-label={`Select ${[r.firstName, r.lastName].filter(Boolean).join(" ") || "this person"}`}
                    className="mt-1 h-5 w-5 shrink-0"
                  />
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <Link
                        to={`/outreach/recipients/${r.id}`}
                        className="text-base font-semibold text-brand-700 hover:underline"
                      >
                        {[r.firstName, r.lastName].filter(Boolean).join(" ") || "(no name)"}
                      </Link>
                      <TypeChip type={r.type} />
                    </div>
                    <div className="mt-0.5 text-xs text-slate-500">
                      {r.dealerName}
                      {r.eventDate ? ` · ${new Date(r.eventDate).toLocaleDateString()}` : ""}
                      {r.vehicle ? ` · ${r.vehicle}` : ""}
                    </div>
                    <div className="mt-1 break-all text-xs text-slate-500">
                      {[r.email, r.phone].filter(Boolean).join(" · ") || "—"}
                    </div>
                    <div className="mt-2 flex flex-wrap items-center gap-1.5">
                      <StatusBadge status={r.sendStatus} skipReason={r.skipReason} />
                      <SurveyBadge recipient={r} />
                    </div>
                  </div>
                </div>
                <div className="mt-2 flex items-center justify-between gap-2 border-t border-slate-100 pt-2">
                  <span className="text-[11px] text-slate-400">
                    {r.updatedAt ? `Updated ${new Date(r.updatedAt).toLocaleString()}` : ""}
                  </span>
                  {rowAction(r)}
                </div>
              </li>
            );
          })}
        </ul>
      </div>

      {/* Phone: the approve/skip buttons sit above the list, so once someone is
          scrolled into the list the selection would have no visible action. */}
      {selectionActionable && (
        <div className="fixed inset-x-0 bottom-0 z-20 flex items-center gap-2 border-t border-slate-200 bg-white/95 px-4 py-3 shadow-[0_-4px_16px_rgba(0,0,0,0.08)] backdrop-blur md:hidden">
          <span className="text-sm font-medium text-slate-700">{selected.size} selected</span>
          <div className="ml-auto flex items-center gap-2">
            <button
              onClick={() => void skipSelected()}
              disabled={busy}
              className="rounded-lg border border-slate-300 px-3 py-2 text-sm text-slate-700 disabled:opacity-40"
            >
              {status === "approved" ? "Remove" : "Skip"}
            </button>
            {status === "pending" && (
              <button
                onClick={() => void approveSelected()}
                disabled={busy}
                className="rounded-lg bg-brand-600 px-4 py-2 text-sm font-medium text-white disabled:opacity-40"
              >
                Approve
              </button>
            )}
          </div>
        </div>
      )}

      <div className="mt-4 hidden overflow-x-auto rounded-xl border border-slate-200 bg-white shadow-sm md:block">
        <table className="min-w-full divide-y divide-slate-200 text-sm">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wide text-slate-500">
              <th className="w-8 px-3 py-2">
                <input type="checkbox" checked={allSelected} onChange={toggleAll} />
              </th>
              <SortHeader label="Customer" col="customer" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Store" col="store" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Type" col="type" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Event date" col="eventDate" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <th className="px-3 py-2">Contact</th>
              <SortHeader label="Status" col="status" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Survey" col="survey" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <SortHeader label="Last updated" col="updated" sortKey={sortKey} sortDir={sortDir} onSort={toggleSort} />
              <th className="px-3 py-2 text-right">Action</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-slate-100">
            {loading && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-slate-500">
                  Loading…
                </td>
              </tr>
            )}
            {!loading && recipients.length === 0 && (
              <tr>
                <td colSpan={10} className="px-3 py-6 text-center text-slate-500">
                  Nothing here.
                </td>
              </tr>
            )}
            {sortedRecipients.map((r) => (
              <tr key={r.id} className="hover:bg-slate-50">
                <td className="px-3 py-2">
                  <input
                    type="checkbox"
                    checked={selected.has(r.id)}
                    onChange={() => toggle(r.id)}
                  />
                </td>
                <td className="px-3 py-2">
                  <Link
                    to={`/outreach/recipients/${r.id}`}
                    className="font-medium text-brand-700 hover:underline"
                  >
                    {[r.firstName, r.lastName].filter(Boolean).join(" ") || "(no name)"}
                  </Link>
                </td>
                <td className="px-3 py-2 text-slate-600">{r.dealerName}</td>
                <td className="px-3 py-2">
                  <TypeChip type={r.type} />
                </td>
                <td className="px-3 py-2 text-slate-600">
                  {r.eventDate ? new Date(r.eventDate).toLocaleDateString() : "—"}
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {[r.email, r.phone].filter(Boolean).join(" · ") || "—"}
                </td>
                <td className="px-3 py-2">
                  <StatusBadge status={r.sendStatus} skipReason={r.skipReason} />
                </td>
                <td className="px-3 py-2">
                  <SurveyBadge recipient={r} />
                </td>
                <td className="px-3 py-2 text-xs text-slate-500">
                  {r.updatedAt ? new Date(r.updatedAt).toLocaleString() : "—"}
                </td>
                <td className="px-3 py-2 text-right">{rowAction(r)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* What the page is NOT showing. This queue asked for 200 rows and
          disclosed nothing about the rest, so with 1,291 pending recipients
          everything past the first 200 was unreachable — on the screen where
          approving sends a real text. */}
      {!loading && total > 0 && (
        <div className="mt-3 flex flex-wrap items-center justify-center gap-3 pb-20 text-center md:pb-0">
          <span className="text-xs text-slate-500">
            Showing {recipients.length} of {total}
          </span>
          {nextOffset !== null && (
            <button
              type="button"
              onClick={() => void loadMore()}
              disabled={loadingMore}
              className="rounded-lg bg-slate-100 px-3 py-1.5 text-xs font-medium text-slate-700 transition hover:bg-slate-200 disabled:opacity-50"
            >
              {loadingMore
                ? "Loading…"
                : `Load ${Math.min(PAGE_SIZE, total - recipients.length)} more`}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export function StatusBadge({
  status,
  skipReason,
}: {
  status: OutreachSendStatus;
  skipReason: string | null;
}) {
  const styles: Record<OutreachSendStatus, string> = {
    pending: "bg-slate-100 text-slate-600 ring-slate-200",
    approved: "bg-sky-50 text-sky-700 ring-sky-200",
    sending: "bg-sky-50 text-sky-700 ring-sky-200",
    sent: "bg-emerald-50 text-emerald-700 ring-emerald-200",
    partial: "bg-amber-50 text-amber-700 ring-amber-200",
    failed: "bg-rose-50 text-rose-700 ring-rose-200",
    suppressed: "bg-rose-50 text-rose-600 ring-rose-200",
    skipped: "bg-slate-100 text-slate-500 ring-slate-200",
  };
  return (
    <span className="inline-flex items-center gap-1">
      <span
        className={`rounded-full px-2 py-0.5 text-[10px] font-medium uppercase tracking-wide ring-1 ring-inset ${styles[status]}`}
      >
        {status}
      </span>
      {status === "skipped" && skipReason && (
        <span className="text-[10px] text-slate-400">
          {skipReasonLabel(skipReason)}
        </span>
      )}
    </span>
  );
}
